import type {
  AiGeneratedContent,
  ErrorType,
  MultiContentAiGeneration,
  PromptBoxConfigOptionValue,
  PromptBoxConfigOptionValueEnum,
  RecentlyGeneratedAiContent} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {
  AiPromptBoxVariant,
  AiTextErrorTypes,
  EmailCampaignSubjectTone,
  HasEmojis,
  HashtagUsage,
  LengthCategory,
  PromptBoxConfigOptionType,
  Purpose,
  Tone,
} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {ListItemStorage} from '@Components/list/list.types';

export const POPUP_OFFSET_ON_MOBILE = 4;
export const POPUP_OFFSET_ON_DESKTOP = -4;
export const DEFAULT_PROMPT_BOX_MODAL_HEIGHT = '264px';
export const MONO_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT = '300px';
export const MULTI_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT = '464px';
export const MULTI_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT_WITH_ERROR = '524px';
export const CONTENT_GENERATED_PROMPT_BOX_MODAL_HEIGHT = '532px';

export const enumToInternationalizedString = (value: PromptBoxConfigOptionValue | null): string => {
  if (!value) {
    return '';
  }

  switch (value) {
    case EmailCampaignSubjectTone.Emotional:
      return window.i18next.t('pmwjs_emotional');
    case EmailCampaignSubjectTone.Professional:
      return window.i18next.t('pmwjs_professional');
    case EmailCampaignSubjectTone.Funny:
      return window.i18next.t('pmwjs_funny');
    case EmailCampaignSubjectTone.Urgent:
      return window.i18next.t('pmwjs_urgent');
    case HasEmojis.Disabled:
      return window.i18next.t('pmwjs_no_emojis');
    case HasEmojis.Enabled:
      return window.i18next.t('pmwjs_use_emojis');
    case HashtagUsage.NO_HASHTAGS:
      return window.i18next.t('pmwjs_no_hashtags');
    case HashtagUsage.USE_HASHTAGS:
      return window.i18next.t('pmwjs_use_hashtags');
    case LengthCategory.LONG:
      return window.i18next.t('pmwjs_long');
    case LengthCategory.MEDIUM:
      return window.i18next.t('pmwjs_medium');
    case LengthCategory.SHORT:
      return window.i18next.t('pmwjs_short');
    case Purpose.GenerateInterest:
      return window.i18next.t('pmwjs_generate_interest');
    case Purpose.AskAQuestion:
      return window.i18next.t('pmwjs_ask_a_question');
    case Purpose.DriveAction:
      return window.i18next.t('pmwjs_drive_action');
    case Tone.CONFIDENT:
      return window.i18next.t('pmwjs_confident');
    case Tone.NEUTRAL:
      return window.i18next.t('pmwjs_neutral');
    case Tone.EMPOWERING:
      return window.i18next.t('pmwjs_empowering');
    case Tone.EXCITED:
      return window.i18next.t('pmwjs_excited');
    case Tone.WITTY:
      return window.i18next.t('pmwjs_witty');
    default:
      return '';
  }
};

export const getSelectedValue = (selectedOptions: ListItemStorage[] | undefined, defaultValue: PromptBoxConfigOptionValue): string => {
  return selectedOptions && selectedOptions.length > 0 ? getOptionValueFromItemId(selectedOptions[0].id) : defaultValue;
};

export const getOptionValueFromItemId = (itemId: string): PromptBoxConfigOptionValue => {
  return itemId.split('-')[0] as PromptBoxConfigOptionValue;
};

export const getOptionTypeFromListId = (listId: string): PromptBoxConfigOptionType => {
  const optionType = listId.split('-').pop();

  if (!optionType) {
    throw new Error('Invalid listId provided to getOptionTypeFromListId');
  }

  return optionType as PromptBoxConfigOptionType;
};

export const getListIdForConfigOption = (uniqueId: string, configType: PromptBoxConfigOptionType): string => {
  return `${uniqueId}-list-${configType}`;
};

export const getListItemIdForCofigOption = (uniqueId: string, optionValue: PromptBoxConfigOptionValue): string => {
  return `${optionValue}-${uniqueId}-list-item`;
};

export const isMultiContentAiGeneration = (content: AiGeneratedContent): boolean => {
  return (content as MultiContentAiGeneration).subtext !== undefined;
};

export const isRecentAiGeneration = (content: AiGeneratedContent): boolean => {
  return (content as RecentlyGeneratedAiContent)?.id !== undefined && (content as RecentlyGeneratedAiContent)?.id !== '' && !!(content as RecentlyGeneratedAiContent)?.id;
};

export const hasMultiContent = (variant: AiPromptBoxVariant): boolean => {
  return [AiPromptBoxVariant.MULTICONTENT].includes(variant);
};

export const getErrorMessageFromCode = (errorCode: AiTextErrorTypes): string => {
  switch (errorCode) {
    case AiTextErrorTypes.NoCaption: // during alteration
      return 'pmwjs_please_enter_caption_to_proceed';
    case AiTextErrorTypes.LimitReached:
      return 'pmwjs_daily_request_limit_reached';
    case AiTextErrorTypes.SpammyPrompt:
      return 'pmwjs_prompt_appears_to_be_spam';
    case AiTextErrorTypes.PromptHasSuspiciousWords:
      return 'pmwjs_please_refrain_from_prohibited_language';
    case AiTextErrorTypes.InvalidPrompt:
      return 'pmwjs_prompt_does_not_comply_with_guidelines';
    case AiTextErrorTypes.NoPrompt:
      return 'pmwjs_please_enter_prompt_to_proceed';
    case AiTextErrorTypes.UserBlacklisted:
      return 'pmwjs_access_restricted_due_to_unusual_activity';
    case AiTextErrorTypes.OpenAiRateLimitHit:
      return 'pmwjs_ai_rate_limit_hit_error';
    case AiTextErrorTypes.NoSubject: // during alteration
      return 'pmwjs_enter_subject_to_proceed';
    case AiTextErrorTypes.NoTextField:
      return 'pmwjs_add_text_field_to_proceed';
    case AiTextErrorTypes.LowCredits:
      return 'pmwjs_low_credits_buy_more_to_continue';
    case AiTextErrorTypes.NoImage:
      return 'pmwjs_add_image_to_proceed';
    case AiTextErrorTypes.ImageGenerationError:
      return 'pmwjs_one_or_more_image_generation_failed';
    default:
      return 'pmwjs_error_occurred_retry';
  }
};

export const getRelevantIconClass = (type: PromptBoxConfigOptionType): string => {
  // Add a new switch case for PromptBoxConfigOptionType
  switch (type) {
    case PromptBoxConfigOptionType.TONE:
    case PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE:
      return 'icon-voice-profile';
    case PromptBoxConfigOptionType.HASHTAGS:
      return 'icon-hash';
    case PromptBoxConfigOptionType.LENGTH:
    case PromptBoxConfigOptionType.PURPOSE:
      return 'icon-align-left';
    case PromptBoxConfigOptionType.EMOJIS:
      return 'icon-sentiment-very-satisfied';
    default:
      return 'icon-check-circle';
  }
};

export function getEnumFromOption(option: PromptBoxConfigOptionType): PromptBoxConfigOptionValueEnum {
  // Add a new switch case for PromptBoxConfigOptionType
  switch (option) {
    case PromptBoxConfigOptionType.LENGTH:
      return LengthCategory;
    case PromptBoxConfigOptionType.TONE:
      return Tone;
    case PromptBoxConfigOptionType.HASHTAGS:
      return HashtagUsage;
    case PromptBoxConfigOptionType.PURPOSE:
      return Purpose;
    case PromptBoxConfigOptionType.EMOJIS:
      return HasEmojis;
    case PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE:
      return EmailCampaignSubjectTone;
    default:
      throw new Error(`Unhandled option: ${option}`);
  }
}

export const getDefaultValueForOption = (configType: PromptBoxConfigOptionType): PromptBoxConfigOptionValue => {
  // Add a new switch case for PromptBoxConfigOptionType
  switch (configType) {
    case PromptBoxConfigOptionType.LENGTH:
      return LengthCategory.SHORT;
    case PromptBoxConfigOptionType.TONE:
      return Tone.NEUTRAL;
    case PromptBoxConfigOptionType.HASHTAGS:
      return HashtagUsage.USE_HASHTAGS;
    case PromptBoxConfigOptionType.PURPOSE:
      return Purpose.GenerateInterest;
    case PromptBoxConfigOptionType.EMOJIS:
      return HasEmojis.Enabled;
    case PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE:
      return EmailCampaignSubjectTone.Professional;
    default:
      throw new Error('invalid list id passed to getDefaultValueForList()');
  }
};

/**
 * Logs errors that carry no domain error code — real thrown errors (e.g. a TypeError) that the user
 * would otherwise only see as the generic "try again" message, with the underlying cause lost.
 */
const logAiPromptErrorWithoutDomainCode = (error: ErrorType): void => {
  if (!error.code) {
    const context = 'AI prompt box: unexpected error without a domain error code';
    console.error(context, error);
    window.PMW.log(`${context}: ${error.message}`);
  }
};

export const handleErrorAndGetErrorMessage = (error: ErrorType): string | null => {
  logAiPromptErrorWithoutDomainCode(error);

  const intlErrorMessage = getErrorMessageFromCode((error.code as AiTextErrorTypes) ?? AiTextErrorTypes.GeneralError);
  return window.i18next.t(intlErrorMessage);
};

export class AiTextPromptBoxError extends Error {
  code?: AiTextErrorTypes;

  constructor(message: string, code?: AiTextErrorTypes) {
    super(message);
    this.code = code;
  }
}
